import { useMemo } from 'react';
import { useBulkFetchTimelineItems } from '@/lib/queries/loop-planner/useBulkFetchTimelineItems';
import { useGetSchedulesByItemIds } from '@/lib/queries/work-integrations/useGetSchedules';
import type { PlanSchedulingScenarioContext } from '../adapters/types';

export interface DependencyForecastDates {
  forecastedStartDateTime: string | null;
  forecastedFinishDateTime: string | null;
}

export const useDependencyForecastDates = (
  itemIds: number[],
  scenarioContext?: PlanSchedulingScenarioContext,
): Record<number, DependencyForecastDates> => {
  const { data: timelineItems } = useBulkFetchTimelineItems({
    loopId: scenarioContext?.loopId ?? 0,
    scenarioId: scenarioContext?.scenarioId ?? 0,
    itemIds: scenarioContext ? itemIds : [],
  });

  const { data: schedules } = useGetSchedulesByItemIds(
    scenarioContext ? [] : itemIds,
    undefined,
    !scenarioContext,
  );

  return useMemo(() => {
    if (scenarioContext) {
      const forecastByItemId: Record<number, DependencyForecastDates> = {};
      for (const timelineItem of timelineItems ?? []) {
        const metrics = timelineItem.scenarioItemMetrics;
        forecastByItemId[timelineItem.item.itemId] = {
          forecastedStartDateTime: metrics?.forecastedStart ?? null,
          forecastedFinishDateTime: metrics?.forecastedFinish ?? null,
        };
      }
      return forecastByItemId;
    }

    const forecastByItemId: Record<number, DependencyForecastDates> = {};
    for (const [itemId, schedule] of Object.entries(schedules ?? {})) {
      forecastByItemId[Number(itemId)] = {
        forecastedStartDateTime: schedule.forecastedStartDateTime,
        forecastedFinishDateTime: schedule.forecastedFinishDateTime,
      };
    }
    return forecastByItemId;
  }, [scenarioContext, timelineItems, schedules]);
};
